/* eslint-disable max-lines */
import { get, child, runTransaction, update, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'
import { set } from 'lodash-es'
import { proxyMap } from 'valtio/utils'

import * as firebaseConnection from '../firebaseConnection'
import { type TrayCardChyronOnCamera } from '../model/tray-cards/types/chyron-on-camera-model'
import { type TrayCardBreaker } from '../model/tray-cards/types/breaker-model'
import { type TrayCardChyronInfo } from '../model/tray-cards/types/chyron-info-model'
import { type TrayCardChyronSpeaker } from '../model/tray-cards/types/chyron-speaker-model'
import { type TrayCardChyronSpeakersOverview } from '../model/tray-cards/types/chyron-speakers-overview-model'
import { type TrayCardComingUp } from '../model/tray-cards/types/coming-up-model'
import { type TrayCardHandout } from '../model/tray-cards/types/handout-model'
import { type TrayCardImage } from '../model/tray-cards/types/image-model'
import { type TrayCardNote } from '../model/tray-cards/types/note-model'
import {
  type TrayCardPoll,
  type TrayCardPollData,
} from '../model/tray-cards/types/poll-model'
import {
  type TrayCardVideo,
  type TrayCardVideoBody,
} from '../model/tray-cards/types/video-model'
import {
  type TrayCard,
  type TrayCardFirebase,
} from '../model/tray-cards/cards-model'
import { type WriteDTO } from '../typeUtils'
import { type TrayCardAction } from '../model/tray-cards/types/action-card-model'
import { type ActionCardResponse } from '../../ActionCards/blocs/useActionCards'
import { ChyronType, isChyronForTrayCard } from '../model/chyron-model'

import { getOverlayState, removeOverlay } from './useOverlays'
import {
  playbackState as videoPlaybackState,
  stopPlayback,
} from './useVideoPlayback'

const ROOT_NODE = 'tray'

interface TrayState {
  trayCards: Map<string, TrayCard>
  trayCardOrder: string[]
  activeCard: string | null
  lastActiveMediaCard: string | null
}

const trayState = proxy<TrayState>({
  trayCards: proxyMap(),
  trayCardOrder: [],
  activeCard: null,
  lastActiveMediaCard: null,
})

const trayInitState = proxy<{
  trayCardsInitialized: boolean
  lastActiveMediaCardInitialized: boolean
}>({
  trayCardsInitialized: false,
  lastActiveMediaCardInitialized: false,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  if (connection.role === 'producer') {
    // Remove tray cards that were left in a pending state from a previous session once the firebase API is ready
    void removeStaleTrayCards(connection)
  }

  // Adds a Firebase listener to keep track of the active tray card.
  onValue(child(child(connection.room, ROOT_NODE), 'active'), (snapshot) => {
    trayState.activeCard = snapshot.val()
  })

  // Adds a Firebase listener to keep track of the tray cards.
  onValue(
    child(child(connection.room, ROOT_NODE), 'cards'),
    async (snapshot) => {
      const cards: Record<string, TrayCard> = snapshot.val() ?? {}

      const unusedCards = new Set(Object.keys(cards))

      const firstCardEntry = Object.entries(cards).find(
        ([_, card]) =>
          card.adjacent &&
          card.adjacent.init &&
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          card.adjacent.before === undefined
      )

      const orderedCards = [] as TrayCard[]

      if (firstCardEntry) {
        let currentCardId: string | undefined = firstCardEntry[0]
        while (currentCardId !== undefined) {
          const currentCard = cards[currentCardId] as TrayCard | undefined
          // @ts-expect-error — don't have time to figure out the specific types right now
          orderedCards.push({ ...currentCard, id: currentCardId })
          unusedCards.delete(currentCardId)
          currentCardId = currentCard?.adjacent?.after ?? undefined
        }
      }

      if (connection.role === 'producer') {
        if (unusedCards.size > 0) {
          unusedCards.forEach((id) => {
            orderedCards.push({ ...cards[id], id })
          })
          void setCards(orderedCards)
        }
      }

      trayState.trayCardOrder = orderedCards.map((c) => c.id)
      orderedCards.forEach((c) => trayState.trayCards.set(c.id, c))
      trayInitState.trayCardsInitialized = true
    }
  )

  // Adds a Firebase listener to keep track of the last active media tray card.
  onValue(child(child(connection.room, ROOT_NODE), 'lastMedia'), (snapshot) => {
    trayState.lastActiveMediaCard = snapshot.val()
    trayInitState.lastActiveMediaCardInitialized = true
  })

  // This is kind of absurd, but without this listener, the transaction on setActiveTrayCard won't work
  onValue(child(connection.room, ROOT_NODE), () => {})
})

const setCards = async (orderedCards: TrayCard[]) => {
  const connection = await firebaseConnection.get()
  try {
    await runTransaction(
      child(child(connection.room, ROOT_NODE), 'cards'),
      () => {
        return orderedCards.reduce<Record<string, TrayCardFirebase>>(
          (finalObj, { id: cardId, ...card }, index) => {
            const newObj = { ...finalObj }
            const previousCardId = index > 0 ? orderedCards[index - 1].id : null
            const nextCardId =
              index < orderedCards.length - 1
                ? orderedCards[index + 1].id
                : null
            newObj[cardId] = {
              ...card,
              adjacent: {
                init: true,
                before: previousCardId ?? null,
                after: nextCardId ?? null,
              },
            }
            return newObj
          },
          {}
        )
      }
    )
  } catch (err) {
    connection.logError('SET_CARDS', 'Failed to set cards', err)
  }
}

// Actions

/**
 * Sets the active tray card.
 * @param id - The ID of the tray card to be set as active.
 */
const setActiveTrayCard = async (id: string) => {
  const connection = await firebaseConnection.get()

  return runTransaction(
    child(connection.room, 'tray'),
    (
      state: {
        active: string
        lastMedia: string
        cards: Record<string, TrayCardFirebase>
      } | null
    ) => {
      const card = state?.cards[id]
      if (!card) return state
      if (['image', 'video'].includes(card.type)) {
        return { ...state, active: id, lastMedia: id }
      }
      return { ...state, active: id }
    }
  ).catch((err: unknown) =>
    connection.logError(
      'SET_ACTIVE_TRAY_CARD',
      'Failed to set active tray card',
      err
    )
  )
}

/**
 * Updates the card order.
 * @param activeCardId - The ID of the active card.
 * @param overCardId - The ID of the card over which the active card is being moved.
 */
const updateCardOrder = async (activeCardId: string, overCardId?: string) => {
  const connection = await firebaseConnection.get()
  try {
    await runTransaction(
      child(child(connection.room, ROOT_NODE), 'cards'),
      (cards: Record<string, TrayCardFirebase> | undefined) => {
        if (cards) {
          const activeCard = cards[activeCardId]
          const overCard = overCardId ? cards[overCardId] : null

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (activeCard && overCard) {
            const activeBefore = activeCard.adjacent?.before
            const activeAfter = activeCard.adjacent?.after

            const overBefore = overCard.adjacent?.before
            const overAfter = overCard.adjacent?.after

            let reachedTop = false
            let currentCard = activeCard
            let found = false
            while (!reachedTop) {
              const beforeCardId = currentCard.adjacent?.before
              if (beforeCardId) {
                if (beforeCardId === overCardId) {
                  found = true
                  break
                }

                currentCard = cards[beforeCardId]
              } else {
                reachedTop = true
              }
            }

            const direction = found ? 'up' : 'down'

            if (activeBefore) {
              set(cards[activeBefore], 'adjacent.after', activeAfter ?? null)
            }

            if (activeAfter) {
              set(cards[activeAfter], 'adjacent.before', activeBefore ?? null)
            }

            if (direction === 'up') {
              if (overBefore) {
                set(cards[overBefore], 'adjacent.after', activeCardId)
              }
              set(cards[activeCardId], 'adjacent.before', overBefore ?? null)

              set(cards[activeCardId], 'adjacent.after', overCardId)
              if (overCardId) {
                set(cards[overCardId], 'adjacent.before', activeCardId)
              }
            } else {
              if (overAfter) {
                set(cards[overAfter], 'adjacent.before', activeCardId)
              }
              set(cards[activeCardId], 'adjacent.after', overAfter ?? null)

              set(cards[activeCardId], 'adjacent.before', overCardId)
              if (overCardId) {
                set(cards[overCardId], 'adjacent.after', activeCardId)
              }
            }
          }
        }
        return cards
      }
    )
  } catch (err) {
    connection.logError('UPDATE_CARD_ORDER', 'Failed to update card order', err)
  }
}

/**
 * Adds a tray card.
 * @param card - The tray card to be added.
 * @param previousCardId - The card after which to insert the new one
 */
const addTrayCard = async (
  { id, ...card }: TrayCard,
  previousCardId?: string
) => {
  const connection = await firebaseConnection.get()
  try {
    const result = await runTransaction(
      child(child(connection.room, ROOT_NODE), 'cards'),
      (cards: Record<string, TrayCardFirebase> | undefined) => {
        const newCards = cards ?? {}

        let nextCardId = null

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (previousCardId && newCards[previousCardId]) {
          nextCardId = newCards[previousCardId].adjacent?.after ?? null
          set(newCards[previousCardId], 'adjacent.after', id)

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (nextCardId && newCards[nextCardId]) {
            set(newCards[nextCardId], 'adjacent.before', id)
          }
        }

        const nowTimestamp = Date.now()

        newCards[id] = {
          ...card,
          creation: nowTimestamp,
          adjacent: {
            init: true,
            before: previousCardId ?? null,
            after: nextCardId ?? null,
          },
        }

        return newCards
      }
    )

    if (result.committed) {
      return true
    }
  } catch (err) {
    connection.logError('ADD_TRAY_CARD', 'Failed to add tray card', err)
  }

  return false
}

/**
 * Check if the tray card exists
 * @param id - The ID of the tray card to be checked.
 */
const hasTrayCard = async (id: string) => {
  const connection = await firebaseConnection.get()
  try {
    void get(child(child(connection.room, ROOT_NODE), `cards/${id}`))
    const snapshot = await get(
      child(child(connection.room, ROOT_NODE), `cards/${id}`)
    )
    return snapshot.exists()
  } catch (err) {
    connection.logError('HAS_TRAY_CARD', 'Failed to check tray card', err)
  }
  return false
}

/**
 * Removes a tray card.
 * @param id - The ID of the tray card to be removed.
 */
const removeTrayCard = async (id: string) => {
  const connection = await firebaseConnection.get()
  const trayCard = trayState.trayCards.get(id)
  try {
    const overlay = getOverlayState()

    const shouldRemoveOverlay =
      overlay &&
      (isChyronForTrayCard(overlay, id) ||
        (overlay.type === ChyronType.PollResult &&
          trayCard?.type === 'poll' &&
          trayCard.poll?.id &&
          overlay.id === String(trayCard.poll.id)))

    if (shouldRemoveOverlay) {
      await removeOverlay()
    }

    const traySnapshot = await get(child(connection.room, ROOT_NODE))

    if (!traySnapshot.exists()) return

    const tray = traySnapshot.val()

    if (tray) {
      const { active, cards } = tray
      const card = cards[id]
      let activeCardId = active ?? null
      if (card) {
        const { before, after } = card.adjacent
        if (before) {
          set(cards[before], 'adjacent.after', after ?? null)

          // if the card is active, set the active card to the previous card
          if (active && active === id) {
            activeCardId = before
          }
        }
        if (after) {
          set(cards[after], 'adjacent.before', before ?? null)
        }

        cards[id] = null
      }

      const updates: {
        cards: Record<string, TrayCard>
        active: string | null
      } = {
        cards,
        active: activeCardId,
      }

      await update(child(connection.room, ROOT_NODE), updates)
    }
  } catch (err) {
    connection.logError('REMOVE_TRAY_CARD', 'Failed to remove tray card', err)
  }
}

/**
 * Updates a tray card.
 * @param id - The ID of the tray card to be updated.
 * @param card - The partial or complete tray card object.
 */
const updateTrayCard = async (
  id: string,
  card: Partial<WriteDTO<TrayCard>>
) => {
  const connection = await firebaseConnection.get()

  const newChanges = { ...card, lastEdit: Date.now() }
  try {
    await update(
      child(child(child(connection.room, ROOT_NODE), 'cards'), id),
      newChanges
    )
  } catch (err) {
    connection.logError('UPDATE_TRAY_CARD', 'Failed to update tray card', err)
  }
}

// delete tray cards that were left in a pending state from a previous session
// only producers should do this
const removeStaleTrayCards = async (
  connection: firebaseConnection.FirebaseConnection
) => {
  try {
    await runTransaction(
      child(child(connection.room, ROOT_NODE), 'cards'),
      (cards: Record<string, TrayCard>) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (cards) {
          const entries = Object.entries(cards)
          for (let i = 0; i < entries.length; i += 1) {
            const [id, card] = entries[i]

            if (
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              card &&
              'participantId' in card &&
              card.participantId &&
              card.participantId === connection.participantId &&
              'sessionToken' in card &&
              card.sessionToken !== connection.sessionId
            ) {
              if (card.adjacent) {
                const { before, after } = card.adjacent
                if (before) {
                  set(cards[before], 'adjacent.after', after ?? null)
                }
                if (after) {
                  set(cards[after], 'adjacent.before', before ?? null)
                }

                // eslint-disable-next-line no-param-reassign,@typescript-eslint/no-dynamic-delete
                delete cards[id]
              }
            }
          }
        }
        return cards
      }
    )
  } catch (err) {
    // maybe we should report the error but it's not critical
    // the next connection will try to remove the stale cards
    connection.logError(
      'TRAY_REMOVE_STALE_CARDS',
      'Failed to remove stale tray cards',
      err
    )
  }
}

export const useTrayState = () => useSnapshot(trayState)

const useTray = () => {
  const { trayCards, trayCardOrder } = useSnapshot(trayState)

  return {
    cards: trayCards,
    cardOrder: trayCardOrder,
  }
}

const useActiveTrayCard = () => useTrayState().activeCard

const useActiveTrayCardIndex = () => {
  const activeCardId = useSnapshot(trayState).activeCard
  return useTrayCardIndex(activeCardId)
}

const useTrayCardState = (id: string) => {
  const activeCardId = useSnapshot(trayState).activeCard
  const activeCardIdx = useTrayCardIndex(activeCardId)
  const cardIndex = useTrayCardIndex(id)

  if (id === activeCardId) return 'active'
  if (cardIndex < activeCardIdx) return 'done'
  return 'pending'
}

const useTrayCard = (id: string | null) => {
  const cards = useSnapshot(trayState.trayCards)

  if (!id) return undefined
  return cards.get(id)
}

const useLastActiveMediaTrayCard = () => {
  const tray = useSnapshot(trayState)
  if (!tray.lastActiveMediaCard) return null
  return tray.trayCards.get(tray.lastActiveMediaCard) as
    | TrayCardImage
    | TrayCardVideo
}

const useTrayCardIndex = (id: string | null) => {
  const cardOrder = useSnapshot(trayState.trayCardOrder)
  if (id === null) return -1
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return cardOrder?.findIndex((cardId) => cardId === id)
}

function addBreakerCard(label: string, id?: string): TrayCardBreaker {
  const card: TrayCardBreaker = {
    type: 'breaker',
    id: crypto.randomUUID(),
    label,
  }

  void addTrayCard(card, id)
  return card
}

function updateBreakerCard(id: string, label: string) {
  void updateTrayCard(id, { label })
}

const addActionCard = (actionCardId?: number, previousCardId?: string) => {
  const card: TrayCardAction = {
    type: 'action',
    id: crypto.randomUUID(),
    isActive: false,
    actionCardId: undefined,
  }
  if (actionCardId) {
    card.actionCardId = actionCardId
  }

  void addTrayCard(card, previousCardId)
  return card
}

const disableLastActiveCard = async () => {
  const connection = await firebaseConnection.get()
  try {
    const snapshot = await get(
      child(child(connection.room, ROOT_NODE), 'cards')
    )
    const cards: Record<string, TrayCardFirebase> = snapshot.val() ?? {}

    const updates: Record<string, Partial<TrayCardAction>> = {}
    Object.entries(cards).forEach(([id, card]) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (card && card.type === 'action') {
        updates[id] = { ...card, isActive: false, type: 'action' }
      }
    })

    if (Object.keys(updates).length > 0) {
      await update(child(child(connection.room, ROOT_NODE), 'cards'), updates)
    }
  } catch (err) {
    connection.logError(
      'UPDATE_ALL_ACTION_CARDS_IS_ACTIVE',
      'Failed to update isActive for action cards',
      err
    )
  }
}

const editActionCard = (id: string, actionCard: ActionCardResponse) => {
  return updateTrayCard(id, {
    actionCardId: actionCard.id,
    title: actionCard.title ?? null,
    description: actionCard.description ?? null,
    image: actionCard.imageUrl ?? null,
    cta: {
      text: actionCard.submitLabel ?? null,
      url: actionCard.submitTargetUrl ?? null,
    },
    isActive: actionCard.isActive,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    answerCount: actionCard.answerCount ?? 0,
    options:
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      actionCard.fields?.map((field, index) => ({
        type: field.type,
        label: field.label,
        id: field.id,
        sortKey: index,
      })) ?? [],
  })
}

function addNoteCard(label: string, id?: string): TrayCardNote {
  const card: TrayCardNote = {
    type: 'note',
    id: crypto.randomUUID(),
    label,
  }
  void addTrayCard(card, id)
  return card
}

function addEmptyNoteCard(id?: string): TrayCardNote {
  return addNoteCard('', id)
}

function updateNoteCard(id: string, label: string) {
  void updateTrayCard(id, { label })
}

function updateComingUpCard(id: string, text: string) {
  void updateTrayCard(id, { text })
}

/**
 * Adds several video cards in one go, keeping the order they are given in.
 * @param videos - The videos to add a card for.
 * @param previousCardId - The card the first new card is placed after.
 */
async function addVideoCards(
  videos: Array<Omit<TrayCardVideoBody, 'type'>>,
  previousCardId?: string
) {
  let previousId = previousCardId

  for (const video of videos) {
    const card: TrayCardVideo = {
      ...video,
      type: 'video',
      id: crypto.randomUUID(),
    }

    // eslint-disable-next-line no-await-in-loop
    const wasAdded = await addTrayCard(card, previousId)

    // A failed insertion would leave every later card pointing at a card that
    //  does not exist, so the batch stops here.
    if (!wasAdded) return

    previousId = card.id
  }
}

function updateVideoCard(
  id: string,
  videoId: number,
  label: string,
  thumbnail: string,
  playback: string,
  token: string
) {
  if (videoPlaybackState.command?.cardId === id) void stopPlayback()

  void updateTrayCard(id, {
    videoId,
    label,
    thumbnail,
    playback,
    token,
    progress: 0,
  })
}

/**
 * Stores whether the video of a card should loop during playback. Written by
 *  producers, reacted to by headless, and deliberately not counted as a card
 *  edit.
 * @param id - The tray card identifier.
 * @param shouldLoop - Whether the video should loop.
 */
async function setVideoCardLooping(id: string, shouldLoop: boolean) {
  if (trayState.trayCards.get(id)?.type !== 'video') return

  const connection = await firebaseConnection.get()

  try {
    // A transaction, so a card removed in the meantime is not recreated.
    await runTransaction(
      child(child(child(connection.room, ROOT_NODE), 'cards'), id),
      (card: TrayCardFirebase | null) => {
        if (!card || card.type !== 'video') return undefined

        return { ...card, shouldLoop }
      }
    )
  } catch (err) {
    connection.logError(
      'SET_VIDEO_CARD_LOOPING',
      'Failed to store the video loop setting',
      err
    )
  }
}

/**
 * Reads the position a video card resumes playback from.
 * @param id - The tray card identifier.
 * @returns The stored progress, or 0 when the card has none.
 */
function getVideoCardProgress(id: string) {
  const card = trayState.trayCards.get(id)

  return card?.type === 'video' ? (card.progress ?? 0) : 0
}

/**
 * Stores the position a video card resumes playback from. Written by headless
 *  while the video plays, and deliberately not counted as a card edit. The
 *  write is skipped when the card is gone or no longer holds the video, so a
 *  removed card is not recreated and a replaced video keeps its fresh start.
 * @param id - The tray card identifier.
 * @param photoId - The video the progress belongs to.
 * @param progress - The position to resume from.
 */
async function setVideoCardProgress(
  id: string,
  photoId: string,
  progress: number
) {
  const card = trayState.trayCards.get(id)

  if (card?.type !== 'video' || `${card.videoId}` !== photoId) return

  const connection = await firebaseConnection.get()

  try {
    // A transaction, so a deletion or video replacement racing with this
    //  write wins safely: the card is re-validated against the server value.
    await runTransaction(
      child(child(child(connection.room, ROOT_NODE), 'cards'), id),
      (current: TrayCardFirebase | null) => {
        if (
          !current ||
          current.type !== 'video' ||
          !('videoId' in current) ||
          String(current.videoId) !== photoId
        ) {
          return undefined
        }

        return { ...current, progress }
      }
    )
  } catch (err) {
    connection.logError(
      'SET_VIDEO_CARD_PROGRESS',
      'Failed to store the video playback progress',
      err
    )
  }
}

async function addImageCard(
  thumbnail: string,
  participantId: number,
  label: string,
  id?: string
): Promise<TrayCardImage | undefined> {
  const connection = await firebaseConnection.get()

  const card: TrayCardImage = {
    type: 'image',
    id: crypto.randomUUID(),
    thumbnail,
    label,
    participantId,
    sessionId: connection.sessionId,
  }
  const wasSuccessful = await addTrayCard(card, id)
  return wasSuccessful ? card : undefined
}

async function addEmptyImageCard(
  participantId: number,
  label: string,
  id?: string
) {
  return addImageCard('', participantId, label, id)
}

async function updateImageCard(id: string, thumbnail: string) {
  void updateTrayCard(id, {
    sessionId: null,
    participantId: null,
    label: null,
    thumbnail,
  })
}

function addHandoutCard(
  filename: string,
  hidden: boolean,
  fileSize: number,
  iconName:
    | 'audio'
    | 'video'
    | 'presentation'
    | 'image'
    | 'document'
    | 'generic',
  id?: string
) {
  const card: TrayCardHandout = {
    type: 'handout',
    id: crypto.randomUUID(),
    filename,
    hidden,
    fileSize,
    iconName,
  }
  void addTrayCard(card, id)
  return card
}

function addChyronSpeakerCard(id?: string) {
  const card: TrayCardChyronSpeaker = {
    type: 'chyron-speaker',
    id: crypto.randomUUID(),
  }
  void addTrayCard(card, id)
  return card
}

function updateChyronSpeakerCard(id: string, speakerId: string) {
  void updateTrayCard(id, { speakerId })
}

function addChyronSpeakersOverviewCard(id?: string) {
  const card: TrayCardChyronSpeakersOverview = {
    type: 'chyron-speakers-overview',
    id: crypto.randomUUID(),
  }
  void addTrayCard(card, id)
  return card
}

function addChyronInfoCard(id?: string) {
  const card: TrayCardChyronInfo = {
    type: 'chyron-info',
    id: crypto.randomUUID(),
  }
  void addTrayCard(card, id)
  return card
}

function addChyronOnCamera(id?: string) {
  const card: TrayCardChyronOnCamera = {
    type: 'chyron-on-camera',
    id: crypto.randomUUID(),
  }
  void addTrayCard(card, id)
  return card
}

function addChyronComingUpCard(id?: string) {
  const card: TrayCardComingUp = {
    type: 'coming-up',
    id: crypto.randomUUID(),
    text: '',
  }
  void addTrayCard(card, id)
  return card
}

function addPollCard(id?: string) {
  const card: TrayCardPoll = {
    type: 'poll',
    id: crypto.randomUUID(),
  }
  void addTrayCard(card, id)
  return card
}

function updatePollCard(id: string, poll: TrayCardPollData) {
  void updateTrayCard(id, { poll })
}

function changeCardOrder(activeCardId: string, overCardId: string) {
  void updateCardOrder(activeCardId, overCardId)
}

function removeCard(id: string) {
  if (videoPlaybackState.command?.cardId === id) void stopPlayback()

  return removeTrayCard(id)
}

function setActiveCard(id: string) {
  void setActiveTrayCard(id)
}

function cardIsOfType(
  cardId: string | null,
  cardType?: TrayCard['type'] | TrayCard['type'][]
) {
  if (!cardId) return false

  const card = trayState.trayCards.get(cardId)
  if (card) {
    switch (typeof cardType) {
      case 'string':
        if (cardType === card.type) return true
        break

      case 'object':
        if (cardType.includes(card.type)) return true
        break

      case 'undefined':
        return true
      default:
    }
  }
  return false
}

function activateNext(cardType?: TrayCard['type'] | TrayCard['type'][]) {
  if (trayState.activeCard === null) {
    void setActiveTrayCard(trayState.trayCardOrder[0] ?? null)
  }
  const activeCardIndex = trayState.trayCardOrder.findIndex(
    (cardId) => cardId === trayState.activeCard
  )

  let i = activeCardIndex + 1
  while (i < trayState.trayCardOrder.length) {
    const cardId = trayState.trayCardOrder[i]
    if (cardIsOfType(cardId, cardType)) {
      void setActiveTrayCard(cardId)
      return
    }
    i += 1
  }
}

function activatePrevious(cardType?: TrayCard['type'] | TrayCard['type'][]) {
  if (trayState.activeCard === null) {
    void setActiveTrayCard(trayState.trayCardOrder[0] ?? null)
  }
  const activeCardIndex = trayState.trayCardOrder.findIndex(
    (cardId) => cardId === trayState.activeCard
  )

  let i = activeCardIndex - 1
  while (i > 0) {
    const cardId = trayState.trayCardOrder[i]
    if (cardIsOfType(cardId, cardType)) {
      void setActiveTrayCard(cardId)
      return
    }
    i -= 1
  }
}

export {
  type TrayState,
  trayState,
  trayInitState,
  addBreakerCard,
  updateBreakerCard,
  addNoteCard,
  addEmptyNoteCard,
  updateNoteCard,
  addChyronOnCamera,
  updateComingUpCard,
  addVideoCards,
  updateVideoCard,
  getVideoCardProgress,
  setVideoCardProgress,
  setVideoCardLooping,
  addImageCard,
  addEmptyImageCard,
  updateImageCard,
  addHandoutCard,
  addChyronSpeakerCard,
  updateChyronSpeakerCard,
  addChyronSpeakersOverviewCard,
  addChyronInfoCard,
  addChyronComingUpCard,
  addPollCard,
  updatePollCard,
  changeCardOrder,
  removeCard,
  setActiveCard,
  hasTrayCard,
  cardIsOfType,
  useTrayCardState,
  useTrayCard,
  useTrayCardIndex,
  useTray,
  useActiveTrayCard,
  useActiveTrayCardIndex,
  useLastActiveMediaTrayCard,
  activateNext,
  activatePrevious,
  addActionCard,
  editActionCard,
  disableLastActiveCard,
}
